<hlm-dialog-surface size="lg">
  <hlm-dialog-header>
    <hlm-dialog-icon-badge tone="primary">
      <hlm-icon name="lucidePhone" size="1.1rem" />
    </hlm-dialog-icon-badge>
    <div hlmDialogHeading>
      <h2 hlmDialogTitle>Link number blocks — {{ data.brandName }}</h2>
      <p hlmDialogDescription>
        Search across country, prefix, city, or carrier — then pick the blocks
        to link. Tokens are AND-matched: <span class="font-mono-feat">+49 berlin</span>
        narrows to Berlin DE blocks.
      </p>
    </div>
    <button hlmDialogClose tabindex="-1" (click)="cancel()">
      <hlm-icon name="lucideX" size="1rem" />
    </button>
  </hlm-dialog-header>

  <section hlmDialogBody class="flex flex-col gap-5">

    <!-- Single freeform search -->
    <label class="flex flex-col gap-1.5">
      <span class="font-mono-feat text-xs font-semibold uppercase tracking-[0.10em] text-muted-foreground">
        Search
      </span>
      <input
        hlmInput
        type="text"
        placeholder="+49 berlin · 30 · OPENNUMBERS · 25814"
        autocomplete="off"
        [formControl]="searchControl"
      />
    </label>

    <!-- Result list — fixed height so the modal frame doesn't pulse with
         the filter result count. Inner list scrolls. -->
    <div class="flex h-[26rem] flex-col overflow-hidden rounded-xl border border-border">
      <div class="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-4 py-2 font-mono-feat text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
        <span class="inline-flex items-center gap-2.5">
          @if (!loading() && !loadFailed() && filtered().length > 0) {
            <button
              type="button"
              class="grid h-[1.05rem] w-[1.05rem] shrink-0 place-items-center rounded-sm border-[1.5px] border-border bg-card text-[0.7rem] text-primary-foreground transition-colors hover:border-primary/60"
              [class.bg-primary]="filteredSelectionState() !== 'none'"
              [class.border-primary]="filteredSelectionState() !== 'none'"
              [attr.aria-label]="filteredSelectionState() === 'all' ? 'Clear visible selection' : 'Select all visible'"
              (click)="toggleSelectAllFiltered()"
            >
              @switch (filteredSelectionState()) {
                @case ('all') { ✓ }
                @case ('some') { – }
              }
            </button>
          }
          <span>Available blocks</span>
        </span>
        <span class="inline-flex items-center gap-2">
          @if (loading()) {
            <hlm-spinner size="12" />
            <span class="normal-case tracking-normal">Loading…</span>
          } @else if (!loadFailed()) {
            <span class="normal-case tracking-normal">
              {{ filtered().length }} of {{ allBlocks().length }} match{{ filtered().length === 1 ? '' : 'es' }}
            </span>
          }
        </span>
      </div>

      <div class="flex-1 overflow-y-auto">
        @if (loading()) {
          <div class="flex items-center justify-center gap-2 p-8 text-sm text-muted-foreground">
            <hlm-spinner size="16" /> Loading available blocks…
          </div>
        } @else if (loadFailed()) {
          <div class="p-8 text-center text-sm text-muted-foreground">
            Couldn't load available blocks. Close the dialog and try again.
          </div>
        } @else if (allBlocks().length === 0) {
          <div class="p-8 text-center text-sm text-muted-foreground">
            No unlinked blocks for this brand. Everything in the catalogue is already linked.
          </div>
        } @else if (filtered().length === 0) {
          <div class="p-8 text-center text-sm text-muted-foreground">
            No matches for "<span class="font-mono-feat">{{ searchControl.value }}</span>".
          </div>
        } @else {
          @for (b of filtered(); track b.key) {
            <div
              role="checkbox"
              tabindex="0"
              [attr.aria-checked]="isSelected(b.key)"
              class="flex cursor-pointer items-center gap-3 border-b border-border/50 px-4 py-2.5 hover:bg-muted/40 focus-visible:bg-muted/60 focus-visible:outline-none last:border-b-0"
              [style.background]="isSelected(b.key) ? 'color-mix(in oklch, var(--primary) 8%, transparent)' : null"
              (click)="toggle(b.key)"
              (keydown.enter)="toggle(b.key); $event.preventDefault()"
              (keydown.space)="toggle(b.key); $event.preventDefault()"
            >
              <span
                class="grid h-[1.05rem] w-[1.05rem] shrink-0 place-items-center rounded-sm border-[1.5px] border-border bg-card text-[0.7rem] text-primary-foreground"
                [class.bg-primary]="isSelected(b.key)"
                [class.border-primary]="isSelected(b.key)"
              >
                @if (isSelected(b.key)) { ✓ }
              </span>
              <span class="font-mono-feat text-sm font-semibold text-foreground" style="min-width:4rem;">{{ b.vorwahl }}</span>
              <span class="font-mono-feat text-sm text-muted-foreground" style="min-width:4rem;">{{ b.prefix }}</span>
              <span class="flex-1 text-sm">
                {{ b.staOrt }}
                @if (b.staLand) {
                  <span class="text-muted-foreground"> · {{ b.staLand }}</span>
                }
              </span>
              <span class="text-xs text-muted-foreground">{{ b.carrier }}</span>
            </div>
          }
        }
      </div>
    </div>
  </section>

  <footer hlmDialogFooter>
    <span class="text-xs text-muted-foreground">
      @if (selectedCount() > 0) {
        <span class="font-semibold text-foreground">{{ selectedCount() }}</span> selected
      }
    </span>
    <div class="flex items-center gap-2">
      <button hlmBtn variant="outline" type="button" (click)="cancel()">Cancel</button>
      <button
        hlmBtn
        variant="default"
        type="button"
        [disabled]="!canSubmit()"
        (click)="attachSelected()"
      >
        @if (attaching()) {
          <hlm-spinner size="16" /> Linking…
        } @else {
          <hlm-icon name="lucidePlus" size="0.9rem" />
          Link {{ selectedCount() }}
        }
      </button>
    </div>
  </footer>
</hlm-dialog-surface>
